import { useCallback, useRef } from "react";
import {
  getCanvasStageHeightPx,
  getMainCanvasWidthPx,
  getOffCanvasStageWidthPx,
  getStageHeightPxFromCanvasHeight,
} from "./mainCanvasLayout";

/**
 * Ignores small ± few-px oscillation in scrollWidth/scrollHeight (subpixel layout noise)
 * so the horizontal/vertical scrollbar thumb length does not jitter during scroll.
 */
export function useStableScrollExtent(value, thresholdPx = 14) {
  const ref = useRef(value);
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return value;
  }
  if (!Number.isFinite(ref.current) || ref.current <= 0) {
    ref.current = value;
    return value;
  }
  if (Math.abs(value - ref.current) >= thresholdPx) {
    ref.current = value;
  }
  return ref.current;
}

/**
 * Updates scroll metrics for the canvas scroll area
 * @param {React.RefObject} scrollAreaRef - Reference to the scroll area element
 * @param {Function} setScrollMetrics - State setter for scroll metrics
 * @returns {Function} - Callback function to update scroll metrics
 */
export const useUpdateScrollMetrics = (scrollAreaRef, setScrollMetrics) => {
  return useCallback(() => {
    const el = scrollAreaRef.current;
    if (!el) return;

    setScrollMetrics({
      scrollTop: el.scrollTop,
      scrollLeft: el.scrollLeft,
      scrollHeight: el.scrollHeight || 1,
      scrollWidth: el.scrollWidth || 1,
      clientHeight: el.clientHeight || 1,
      clientWidth: el.clientWidth || 1,
    });

  }, [scrollAreaRef, setScrollMetrics]);
};

/**
 * Creates a throttled version of updateScrollMetrics for scroll events
 * @param {Function} updateScrollMetrics - The function to throttle
 * @returns {Function} - Throttled callback function
 */
export const useThrottledUpdateScrollMetrics = (updateScrollMetrics) => {
  return useCallback(() => {
    requestAnimationFrame(updateScrollMetrics);
  }, [updateScrollMetrics]);
};

/**
 * Updates the effective content size for the canvas
 * @param {React.RefObject} contentRef - Reference to content element
 * @param {React.RefObject} scaledContentRef - Reference to scaled content element
 * @param {React.RefObject} scrollAreaRef - Reference to scroll area element
 * @param {Function} setEffectiveContentSize - State setter for effective content size
 * @param {string} canvasHeight - Canvas height percentage
 * @param {number} zoomLevel - Current zoom level
 * @returns {Function} - Callback function to update effective content size
 */
export const useUpdateEffectiveContentSize = (
  contentRef,
  scaledContentRef,
  scrollAreaRef,
  setEffectiveContentSize,
  canvasHeight,
  zoomLevel
) => {
  return useCallback(() => {
    const contentEl = contentRef.current;
    const scaledEl = scaledContentRef.current;
    const containerEl = scrollAreaRef.current;
    if (!containerEl) return;

    let width = 0;
    let height = 0;

    if (scaledEl) {
      // Layout box size (stable while scrolling). getBoundingClientRect includes
      // transform and subpixels and can oscillate — that shrinks/grows spacer → scrollWidth jitter.
      width = Math.ceil(scaledEl.offsetWidth || 0);
      height = Math.ceil(scaledEl.offsetHeight || 0);
    } else if (contentEl) {
      width = Math.ceil(contentEl.offsetWidth || 0);
      height = Math.ceil(contentEl.offsetHeight || 0);
    } else {
      width = containerEl.scrollWidth;
      height = containerEl.scrollHeight;
    }

    // Floor spacer to full triple-stage layout size (unscaled layout px). Do **not** multiply by
    // app zoom — `[data-app-zoom]` uses `transform: scale()`; layout width/height are not `× zoom`.
    // Multiplying here inflated `scrollWidth`/`maxL` vs `scrollLeft` from centering, so the custom
    // horizontal thumb’s ratio `scrollLeft/hScrollable` drifted left as zoom increased.
    try {
      const canvasHeightPercent = parseInt(canvasHeight, 10);
      if (!isNaN(canvasHeightPercent)) {
        const mainStageHeightPx = getStageHeightPxFromCanvasHeight(canvasHeight);
        const offCanvasStageHeightPx = getCanvasStageHeightPx();
        const stageHeightPx = Math.max(mainStageHeightPx, offCanvasStageHeightPx);
        // Horizontal scroll must cover left off-canvas strip + main column + right side span
        // used by centered main-stage positioning.
        const stageWidthPx = Math.floor(
          getOffCanvasStageWidthPx() + getMainCanvasWidthPx(),
        );
        width = Math.max(width, stageWidthPx);
        height = Math.max(height, stageHeightPx);
      }
    } catch (e) {
      // Ignore errors in calculation
    }

    // Ensure spacer is at least as big as the viewport so thumbs do not vanish
    width = Math.max(width, containerEl.clientWidth || 0);
    height = Math.max(height, containerEl.clientHeight || 0);

    setEffectiveContentSize({ width, height });
 
  }, [
    contentRef,
    scaledContentRef,
    scrollAreaRef,
    setEffectiveContentSize,
    canvasHeight,
    zoomLevel,
  ]);
};
